import { useState, useRef, useCallback, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';

export default function PullToRefresh({ onRefresh, children, disabled = false }) {
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const containerRef = useRef(null);
  const startY = useRef(0);
  const currentY = useRef(0);
  
  const PULL_THRESHOLD = 80; // Minimum pull distance to trigger refresh
  const MAX_PULL = 120; // Maximum visual pull distance

  const handleTouchStart = useCallback((e) => {
    if (disabled || isRefreshing) return;
    
    // Only trigger if at top of scroll
    const scrollTop = containerRef.current?.scrollTop || window.scrollY;
    if (scrollTop > 0) return;
    
    startY.current = e.touches[0].clientY;
    setIsPulling(true);
  }, [disabled, isRefreshing]);

  const handleTouchMove = useCallback((e) => {
    if (!isPulling || disabled || isRefreshing) return;
    
    currentY.current = e.touches[0].clientY;
    const diff = currentY.current - startY.current;
    
    if (diff > 0) {
      // Apply resistance to pull
      const resistance = 0.4;
      const distance = Math.min(diff * resistance, MAX_PULL);
      setPullDistance(distance);
      
      // Prevent default scroll when pulling
      if (distance > 10) {
        e.preventDefault();
      }
    }
  }, [isPulling, disabled, isRefreshing]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling || disabled) return;
    
    setIsPulling(false);
    
    if (pullDistance >= PULL_THRESHOLD && onRefresh) {
      setIsRefreshing(true);
      try {
        await onRefresh();
      } catch (error) {
        console.error('Refresh error:', error);
      } finally {
        setIsRefreshing(false);
      }
    }
    
    setPullDistance(0);
  }, [isPulling, pullDistance, onRefresh, disabled]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Add touch event listeners with passive: false to allow preventDefault
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  const pullProgress = Math.min(pullDistance / PULL_THRESHOLD, 1);
  const rotation = pullProgress * 180;

  return (
    <div ref={containerRef} className="relative min-h-full">
      {/* Pull indicator */}
      <div 
        className="absolute left-0 right-0 flex justify-center items-center transition-transform duration-200 z-50"
        style={{ 
          transform: `translateY(${pullDistance - 50}px)`,
          opacity: pullProgress
        }}
      >
        <div className={`
          bg-gradient-to-r from-pink-500 to-purple-500 rounded-full p-2 shadow-lg
          ${isRefreshing ? 'animate-spin' : ''}
        `}>
          <RefreshCw 
            className="w-5 h-5 text-white transition-transform duration-200"
            style={{ transform: `rotate(${isRefreshing ? 0 : rotation}deg)` }}
          />
        </div>
      </div>
      
      {/* Pull text indicator */}
      {(isPulling || isRefreshing) && pullDistance > 20 && (
        <div 
          className="absolute left-0 right-0 flex justify-center items-center z-50 text-xs font-medium"
          style={{ 
            transform: `translateY(${pullDistance + 10}px)`,
            opacity: pullProgress
          }}
        >
          <span className="text-pink-600 bg-white/80 px-3 py-1 rounded-full shadow-sm">
            {isRefreshing 
              ? 'Memuat semula...' 
              : pullProgress >= 1 
                ? 'Lepaskan untuk refresh' 
                : 'Tarik ke bawah untuk refresh'
            }
          </span>
        </div>
      )}
      
      {/* Content with transform during pull */}
      <div 
        className="transition-transform duration-200"
        style={{ 
          transform: `translateY(${isRefreshing ? 50 : pullDistance}px)`
        }}
      >
        {children}
      </div>
    </div>
  );
}
